import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import Loader from "../../common/Loader";
import { quotationService } from "../../../services/quotationService";
import useAccessSettings from "../../../hooks/useInit/useAccessSettings";
import { userAccessEnum } from "../../../config/accessEnum";
import EnvoyerPourSignatureModal from "./EnvoyerPourSignatureModal";
import styles from "./QuotationPdf.module.scss";

// Formate la date d'enregistrement d'une CP (ISO 8601) pour l'affichage.
const formatCpDate = (iso) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat("dd/MM/yyyy 'à' HH'h'mm") : "—";
};

const QuotationPdf = ({ quotation, dossierUuid, client, date, onSignatureChange }) => {
  const { accessActions } = useAccessSettings();

  const canView = accessActions.canI({
    action: userAccessEnum.VISUALISER_DOSSIER,
  }).granted;
  const canSave = accessActions.canI({
    action: userAccessEnum.MODIFIER_DOSSIER,
  }).granted;

  // PDF actuellement affiché dans l'iframe (CP consultée, prévisualisée ou signée).
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isConsulting, setIsConsulting] = useState(false);
  // LS-702 (amélioration) — fusion avec les CGV du client, disponible uniquement à la
  // prévisualisation (aucun document généré) : la fusion est faite côté serveur par
  // l'endpoint de preview, pas côté front.
  const [includeCgv, setIncludeCgv] = useState(false);

  // CP enregistrée côté API (volet latéral + document affiché par « Consulter la CP »).
  const [existingCp, setExistingCp] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(canView);
  const [isGenerating, setIsGenerating] = useState(false);

  // LS-702 — Distinct de `includeCgv` (aperçu) : ce choix est transmis à l'API et persisté
  // côté serveur dans le document envoyé en signature. Un mélange des deux ferait qu'un simple
  // décochage pour prévisualiser purgerait silencieusement le document déjà en attente de
  // signature à la prochaine génération.
  const [includeCgvSignature, setIncludeCgvSignature] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSendingSignature, setIsSendingSignature] = useState(false);

  // LS-702 addendum — état d'envoi en signature de la ligne (garde-fou anti doublon + date
  // affichée). Initialisé depuis `quotation` (QuotationDetailResource), mis à jour localement
  // après un envoi ou une actualisation réussis.
  const [signatureStatus, setSignatureStatus] = useState(
    quotation.signatureStatus ?? null
  );
  const [signatureStatusLabel, setSignatureStatusLabel] = useState(
    quotation.signatureStatusLabel ?? null
  );
  const [signatureEnvoyeeLe, setSignatureEnvoyeeLe] = useState(
    quotation.signatureEnvoyeeLe ?? null
  );
  // LS-692 — canal d'envoi de la signature : "electronique" (Youtrust), "manuel"
  // (le commercial gère l'envoi/retour hors de l'outil) ou null tant qu'aucun envoi
  // n'a eu lieu. Verrouillé au premier envoi : conditionne les actions proposées.
  const [signatureMode, setSignatureMode] = useState(
    quotation.signatureMode ?? null
  );
  const [isRefreshingSignature, setIsRefreshingSignature] = useState(false);
  const [isMarkingManuel, setIsMarkingManuel] = useState(false);
  const [isCancelingManuel, setIsCancelingManuel] = useState(false);
  const [isUploadingSigne, setIsUploadingSigne] = useState(false);

  // LS-702 (recette) — la réf évite que l'identité du callback parent n'invalide
  // `loadExistingCp` (et ne redéclenche le fetch) à chaque re-rendu du parent provoqué
  // par la propagation elle-même.
  const onSignatureChangeRef = React.useRef(onSignatureChange);
  useEffect(() => {
    onSignatureChangeRef.current = onSignatureChange;
  });

  // Applique un statut de signature renvoyé par l'API (endpoints signature et CP) :
  // état local de la modale + remontée au parent, pour que la ligne de devis et une
  // réouverture de la modale reflètent l'état réel sans rechargement de la page.
  const appliquerStatutSignature = React.useCallback((data) => {
    const champs = {
      signatureStatus: data.signatureStatus ?? null,
      signatureStatusLabel: data.signatureStatusLabel ?? null,
      signatureMode: data.signatureMode ?? null,
      signatureEnvoyeeLe: data.signatureEnvoyeeLe ?? null,
    };
    setSignatureStatus(champs.signatureStatus);
    setSignatureStatusLabel(champs.signatureStatusLabel);
    setSignatureMode(champs.signatureMode);
    setSignatureEnvoyeeLe(champs.signatureEnvoyeeLe);
    onSignatureChangeRef.current?.(champs);
  }, []);

  const estManuel = signatureMode === "manuel";

  // Document signé récupéré depuis Youtrust (exposé par l'endpoint `show`), affiché en
  // priorité dans l'aperçu et l'encart de droite quand il existe.
  const documentSigne = existingCp?.documentSigne ?? null;
  // Une nouvelle CP a-t-elle été générée depuis le dernier envoi en signature ? Source de
  // vérité côté serveur (`signatureDocumentRegenere`), alignée avec le garde-fou API.
  const signatureRegenere = existingCp?.signatureDocumentRegenere ?? false;

  // Aucun document généré pour la ligne => le bouton prévisualise au lieu de consulter.
  const consulterLabel = existingCp ? "Consulter la CP" : "Prévisualiser la CP";

  // Révoque l'URL blob affichée dès qu'elle change ou au démontage.
  useEffect(() => {
    if (!pdfUrl) return;
    return () => URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const loadExistingCp = React.useCallback(() => {
    if (!canView || !dossierUuid || !quotation?.uuid) {
      setLoadingExisting(false);
      return Promise.resolve(null);
    }
    setLoadingExisting(true);
    return quotationService
      .getConditionsParticulieres(dossierUuid, quotation.uuid)
      .then((res) => {
        // 404 => pas de CP : corps vide.
        const cp = res.status === 404 || !res.data ? null : res.data;
        setExistingCp(cp);
        // LS-702 (recette) — le statut renvoyé par l'API fait foi : le prop `quotation`
        // peut être périmé (modale rouverte sans rechargement de la page).
        if (cp && "signatureStatus" in cp) {
          appliquerStatutSignature(cp);
        }
        return cp;
      })
      .catch((e) => {
        console.error("Erreur lors du chargement de la CP enregistrée", e);
        return null;
      })
      .finally(() => setLoadingExisting(false));
  }, [canView, dossierUuid, quotation, appliquerStatutSignature]);

  useEffect(() => {
    loadExistingCp();
  }, [loadExistingCp]);

  // Affiche le document de la ligne, sans le régénérer :
  // - aucun document => prévisualisation à la volée (fusion CGV côté serveur si demandée) ;
  // - un document signé => il est affiché en priorité ;
  // - sinon la dernière CP générée.
  // `forcerCpGeneree` : juste après une (re)génération, on affiche la CP fraîche et non
  // l'ancien document signé.
  async function consulterCp(cp = existingCp, forcerCpGeneree = false) {
    setIsConsulting(true);
    setPreviewError(null);
    try {
      let bytes;
      if (!cp) {
        const res = await quotationService.previewConditionsParticulieres(
          dossierUuid,
          quotation.uuid,
          includeCgv
        );
        bytes = res.data;
      } else if (cp.documentSigne && !forcerCpGeneree) {
        const res = await quotationService.downloadConditionsParticulieres(
          dossierUuid,
          quotation.uuid,
          "signe"
        );
        bytes = res.data;
      } else {
        const res = await quotationService.downloadConditionsParticulieres(
          dossierUuid,
          quotation.uuid
        );
        bytes = res.data;
      }

      setPdfUrl(
        URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }))
      );
    } catch (e) {
      console.error("Erreur lors du chargement de la CP", e);
      setPreviewError("Impossible d'afficher le PDF des conditions particulières.");
    } finally {
      setIsConsulting(false);
    }
  }

  // Génère et enregistre une nouvelle CP côté API (remplace la précédente).
  async function generateCp() {
    if (existingCp) {
      const avertissementSignature =
        signatureStatus === "signee"
          ? "\n\nCette ligne est déjà signée : le nouveau document ne pourra pas être envoyé en signature depuis cet écran."
          : estManuel
            ? "\n\nCette ligne est suivie en envoi manuel : régénérer la CP ne change pas son statut de signature. Téléchargez la nouvelle version pour l'envoyer vous-même."
            : signatureStatus === "envoyee"
              ? "\n\nCette ligne a déjà été envoyée en signature : cette régénération vous permettra de la renvoyer (l'envoi précédent sera annulé côté Youtrust)."
              : "";
      const confirmed = window.confirm(
        `Une CP a déjà été enregistrée le ${formatCpDate(
          existingCp.createdAt
        )}.\nCe nouvel enregistrement remplacera définitivement le document précédent.${avertissementSignature}\n\nContinuer ?`
      );
      if (!confirmed) return;
    }
    setIsGenerating(true);
    setPreviewError(null);
    try {
      await quotationService.generateConditionsParticulieres(
        dossierUuid,
        quotation.uuid,
        includeCgvSignature
      );
      toast.success("Conditions particulières générées.");
      const cp = await loadExistingCp();
      if (cp) await consulterCp(cp, true);
    } catch (e) {
      console.error("Erreur lors de la génération de la CP", e);
      toast.error("Échec de la génération des conditions particulières.");
    } finally {
      setIsGenerating(false);
    }
  }

  // Envoie en signature (Youtrust) le document déjà généré/persisté pour cette ligne — l'API
  // ne régénère jamais rien à l'envoi. Un renvoi n'est possible qu'après une nouvelle
  // génération (l'API annule alors l'ancienne demande).
  async function envoyerEnSignature(signataire) {
    setIsSendingSignature(true);
    try {
      await quotationService.envoyerEnSignature(
        dossierUuid,
        quotation.uuid,
        signataire
      );
      toast.success("Document envoyé en signature.");
      appliquerStatutSignature({
        signatureStatus: "envoyee",
        signatureStatusLabel: "Proposition envoyée",
        signatureMode: "electronique",
        signatureEnvoyeeLe: new Date().toISOString(),
      });
      await loadExistingCp();
      return true;
    } catch (e) {
      console.error("Erreur lors de l'envoi en signature", e);
      toast.error(
        e.response?.data?.message || "Échec de l'envoi en signature."
      );
      return false;
    } finally {
      setIsSendingSignature(false);
    }
  }

  // LS-702 addendum — interroge Youtrust à la demande (sans attendre le webhook) et récupère
  // le document signé s'il est disponible.
  async function actualiserSignature() {
    setIsRefreshingSignature(true);
    try {
      const res = await quotationService.actualiserSignature(
        dossierUuid,
        quotation.uuid
      );
      appliquerStatutSignature(res.data);
      if (res.data.signatureStatus === "signee") {
        toast.success("Document signé récupéré.");
        // Récupère le document signé pour l'aperçu et l'encart de droite.
        await loadExistingCp();
      } else {
        toast.info("Toujours en attente de signature.");
      }
    } catch (e) {
      console.error("Erreur lors de l'actualisation du statut de signature", e);
      toast.error(
        e.response?.data?.message ||
          "Échec de l'actualisation du statut de signature."
      );
    } finally {
      setIsRefreshingSignature(false);
    }
  }

  // LS-692 — le commercial a téléchargé la CP et l'envoie lui-même au client :
  // on marque la ligne « Proposition envoyée » en canal manuel, sans envoi externe.
  async function marquerEnvoiManuel() {
    setIsMarkingManuel(true);
    try {
      const res = await quotationService.marquerEnvoiManuel(
        dossierUuid,
        quotation.uuid
      );
      appliquerStatutSignature(res.data);
      toast.success("Ligne marquée comme envoyée (envoi manuel).");
      await loadExistingCp();
    } catch (e) {
      console.error("Erreur lors du marquage en envoi manuel", e);
      toast.error(
        e.response?.data?.message || "Échec du marquage en envoi manuel."
      );
    } finally {
      setIsMarkingManuel(false);
    }
  }

  // LS-692 — remet la ligne à l'état initial (aucun statut de signature) : les deux
  // canaux redeviennent disponibles. Impossible une fois un document signé téléversé.
  async function annulerEnvoiManuel() {
    if (
      !window.confirm(
        "Annuler l'envoi manuel ? La ligne repassera à l'état initial et les deux canaux de signature redeviendront disponibles."
      )
    )
      return;
    setIsCancelingManuel(true);
    try {
      const res = await quotationService.annulerEnvoiManuel(
        dossierUuid,
        quotation.uuid
      );
      appliquerStatutSignature(res.data);
      toast.success("Envoi manuel annulé.");
      // `signatureDocumentRegenere` est recalculé côté serveur une fois le mode effacé.
      await loadExistingCp();
    } catch (e) {
      console.error("Erreur lors de l'annulation de l'envoi manuel", e);
      toast.error(
        e.response?.data?.message || "Échec de l'annulation de l'envoi manuel."
      );
    } finally {
      setIsCancelingManuel(false);
    }
  }

  // LS-692 — téléverse le PDF signé récupéré par le commercial (retour email ou scan).
  // La ligne passe à « Proposition signée » et le document apparaît avec le badge « Signé ».
  async function televerserDocumentSigneManuel(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Le document signé doit être un fichier PDF.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Le document signé ne doit pas dépasser 10 Mo.");
      return;
    }
    setIsUploadingSigne(true);
    try {
      const res = await quotationService.televerserDocumentSigneManuel(
        dossierUuid,
        quotation.uuid,
        file
      );
      appliquerStatutSignature(res.data);
      toast.success("Document signé enregistré.");
      await loadExistingCp();
    } catch (err) {
      console.error("Erreur lors du téléversement du document signé", err);
      toast.error(
        err.response?.data?.message || "Échec du téléversement du document signé."
      );
    } finally {
      setIsUploadingSigne(false);
    }
  }

  // Le devis a-t-il été modifié après l'enregistrement de la CP ?
  // `date` = updatedAt du dossier : indicateur prudent (peut concerner une
  // autre ligne), d'où la formulation « postérieures à l'enregistrement ».
  const cpIsStale = (() => {
    if (!existingCp?.createdAt || !date) return false;
    const saved = DateTime.fromISO(existingCp.createdAt);
    const updated =
      DateTime.fromISO(date).isValid
        ? DateTime.fromISO(date)
        : DateTime.fromFormat(date, "dd/MM/yyyy");
    if (!saved.isValid || !updated.isValid) return false;
    return updated > saved;
  })();

  // Envoi en signature : premier envoi possible dès qu'une CP existe ; renvoi possible
  // uniquement après une nouvelle génération et tant que la ligne n'est pas signée.
  const envoiLabel =
    signatureStatus === "envoyee" && signatureRegenere
      ? "Renvoyer pour signature"
      : "Envoyer pour signature";
  const envoiDisabled =
    !existingCp ||
    isGenerating ||
    isSendingSignature ||
    estManuel ||
    signatureStatus === "signee" ||
    (signatureStatus === "envoyee" && !signatureRegenere);
  const envoiTitle = !existingCp
    ? "Générez d'abord une CP pour cette ligne."
    : estManuel
      ? "Cette ligne est suivie en envoi manuel. Annulez l'envoi manuel pour passer par la signature électronique."
      : signatureStatus === "signee"
        ? "Cette ligne est déjà signée."
        : signatureStatus === "envoyee" && !signatureRegenere
          ? "Déjà envoyée en signature. Régénérez la CP pour pouvoir la renvoyer."
          : undefined;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
      }}
    >
      <div
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: "0 0 auto",
            padding: "8px 12px",
            borderBottom: "1px solid #e0e0e0",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span className="text-muted" style={{ fontSize: 12 }}>
            Conditions particulières
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!existingCp && (
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  margin: 0,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={includeCgv}
                  onChange={(e) => setIncludeCgv(e.target.checked)}
                  style={{ margin: 0 }}
                />
                Aperçu avec CGV
              </label>
            )}

            {canView && (
              <button
                className={styles.btn}
                onClick={() => consulterCp()}
                disabled={loadingExisting || isConsulting || isGenerating}
              >
                {isConsulting ? (
                  <i className="fa fa-spinner fa-spin mr-2" />
                ) : (
                  <i className="fa fa-eye mr-2" />
                )}
                {consulterLabel}
              </button>
            )}

            {canSave && (
              <>
                <span
                  style={{
                    width: 1,
                    alignSelf: "stretch",
                    background: "#e0e0e0",
                  }}
                />

                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    margin: 0,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  title="Fusionne les CGV du client dans le document enregistré et envoyé en signature. Une CP déjà en attente de signature est remplacée si vous régénérez avec un choix différent."
                >
                  <input
                    type="checkbox"
                    checked={includeCgvSignature}
                    onChange={(e) =>
                      setIncludeCgvSignature(e.target.checked)
                    }
                    style={{ margin: 0 }}
                  />
                  Inclure les CGV (document de signature)
                </label>

                <button
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={generateCp}
                  disabled={isGenerating || isConsulting}
                >
                  {isGenerating ? (
                    <i className="fa fa-spinner fa-spin mr-2" />
                  ) : (
                    <i className="fa fa-file-pdf-o mr-2" />
                  )}
                  {existingCp ? "Régénérer la CP" : "Générer la CP"}
                </button>

                <button
                  className={styles.btn}
                  onClick={() => setIsSignatureModalOpen(true)}
                  disabled={envoiDisabled}
                  title={envoiTitle}
                >
                  {isSendingSignature ? (
                    <i className="fa fa-spinner fa-spin mr-2" />
                  ) : (
                    <i className="fa fa-file-signature mr-2" />
                  )}
                  {envoiLabel}
                </button>

                {/* LS-692 — le commercial gère la signature à la main : proposé
                    seulement tant qu'aucun envoi (électronique ou manuel) n'a eu lieu. */}
                {existingCp && signatureStatus === null && (
                  <button
                    className={styles.btn}
                    onClick={marquerEnvoiManuel}
                    disabled={isMarkingManuel || isGenerating}
                    title="Marque la ligne comme envoyée sans passer par la signature électronique : vous envoyez la CP au client vous-même, puis vous téléverserez le document signé."
                  >
                    {isMarkingManuel ? (
                      <i className="fa fa-spinner fa-spin mr-2" />
                    ) : (
                      <i className="fa fa-paper-plane-o mr-2" />
                    )}
                    Marquer comme envoyé (manuel)
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {canSave && signatureStatus !== null && (
          <div
            style={{
              flex: "0 0 auto",
              padding: "6px 12px",
              borderBottom: "1px solid #e0e0e0",
              background: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 10,
              fontSize: 12,
            }}
          >
            <span className="text-muted">
              {signatureStatusLabel || "Envoyée en signature"}
              {estManuel && " (envoi manuel)"}
              {signatureEnvoyeeLe &&
                ` le ${formatCpDate(signatureEnvoyeeLe)}`}
            </span>
            {/* LS-702 — canal électronique : interrogation Youtrust à la demande. */}
            {signatureStatus === "envoyee" && !estManuel && (
              <button
                className={styles.btn}
                onClick={actualiserSignature}
                disabled={isRefreshingSignature}
              >
                {isRefreshingSignature ? (
                  <i className="fa fa-spinner fa-spin mr-2" />
                ) : (
                  <i className="fa fa-refresh mr-2" />
                )}
                Vérifier la signature
              </button>
            )}
            {/* LS-692 — canal manuel : téléversement du PDF signé + annulation. */}
            {signatureStatus === "envoyee" && estManuel && (
              <>
                <label
                  className={styles.btn}
                  style={{ margin: 0, cursor: "pointer" }}
                >
                  {isUploadingSigne ? (
                    <i className="fa fa-spinner fa-spin mr-2" />
                  ) : (
                    <i className="fa fa-upload mr-2" />
                  )}
                  Téléverser le document signé
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={televerserDocumentSigneManuel}
                    disabled={isUploadingSigne}
                    style={{ display: "none" }}
                  />
                </label>
                <button
                  className={styles.btn}
                  onClick={annulerEnvoiManuel}
                  disabled={isCancelingManuel || isUploadingSigne}
                >
                  {isCancelingManuel ? (
                    <i className="fa fa-spinner fa-spin mr-2" />
                  ) : (
                    <i className="fa fa-times mr-2" />
                  )}
                  Annuler l'envoi manuel
                </button>
              </>
            )}
          </div>
        )}
        <div style={{ flex: "1 1 auto", minHeight: 0 }}>
          {previewError ? (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "red",
              }}
            >
              {previewError}
            </div>
          ) : isGenerating ? (
            <Loader text="Génération de la CP en cours…" />
          ) : isConsulting ? (
            <Loader text="Chargement du document…" />
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Conditions particulières"
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          ) : loadingExisting ? (
            <Loader />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: 24,
              }}
              className="text-muted"
            >
              {existingCp
                ? "Cliquez sur « Consulter la CP » pour afficher la dernière version enregistrée."
                : canSave
                  ? "Aucune CP n'a encore été générée pour ce devis. Cliquez sur « Prévisualiser la CP » pour la visualiser, ou « Générer la CP » pour l'enregistrer."
                  : "Aucune CP n'a encore été générée pour ce devis. Cliquez sur « Prévisualiser la CP » pour la visualiser."}
            </div>
          )}
        </div>
      </div>

      {canView && (
        <aside
          style={{
            flex: "0 0 280px",
            borderLeft: "1px solid #e0e0e0",
            background: "#fafafa",
            padding: 16,
            overflowY: "auto",
          }}
        >
          <h5 style={{ marginTop: 0, marginBottom: 2 }}>CP enregistrée</h5>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 14 }}>
            Version archivée sur le serveur
          </p>
          {loadingExisting ? (
            <div className="text-muted">
              <i className="fa fa-spinner fa-spin mr-2" />
              Chargement…
            </div>
          ) : existingCp ? (
            <div>
              {documentSigne && (
                <a
                  href={documentSigne.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Télécharger le document signé"
                  style={{
                    position: "relative",
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                    background: "#fff",
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                    border: "1px solid #cdead5",
                    marginBottom: 14,
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: "#fff",
                      background: "#1e7e34",
                      borderRadius: 10,
                      padding: "2px 8px",
                    }}
                  >
                    <i className="fa fa-check-circle mr-1" />
                    Signé
                  </span>
                  <div
                    style={{
                      height: 56,
                      background:
                        "linear-gradient(110deg, #34c759 0%, #1e7e34 100%)",
                      clipPath: "polygon(0 0, 100% 0, 100% 60%, 0 100%)",
                    }}
                  />
                  <div
                    style={{
                      padding: "4px 16px 16px",
                      marginTop: -18,
                      textAlign: "center",
                    }}
                  >
                    <i
                      className="fa fa-file-pdf-o"
                      style={{
                        fontSize: 40,
                        color: "#1e7e34",
                        background: "#fff",
                        borderRadius: 8,
                        padding: 6,
                      }}
                    />
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        marginTop: 8,
                        wordBreak: "break-all",
                      }}
                    >
                      {documentSigne.fileName || "Document signé"}
                    </div>
                    <div
                      className="text-muted"
                      style={{ fontSize: 12, marginTop: 6 }}
                    >
                      Signé le
                      <br />
                      <strong>{formatCpDate(documentSigne.createdAt)}</strong>
                    </div>
                    <div
                      style={{
                        marginTop: 12,
                        color: "#1e7e34",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      <i className="fa fa-download mr-2" />
                      Télécharger
                    </div>
                  </div>
                  <div
                    style={{
                      height: 32,
                      background: "#111",
                      clipPath: "polygon(0 40%, 100% 0, 100% 100%, 0 100%)",
                    }}
                  />
                </a>
              )}
              {cpIsStale && (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    fontSize: 12,
                    lineHeight: 1.35,
                    color: "#8a5a00",
                    background: "#fff6e5",
                    border: "1px solid #f0d39a",
                    borderRadius: 6,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  <i
                    className="fa fa-exclamation-triangle"
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    Le devis a été modifié après cet enregistrement. La
                    version consultable peut différer de cette version.
                  </span>
                </div>
              )}
              <a
                href={existingCp.url}
                target="_blank"
                rel="noopener noreferrer"
                title="Télécharger la CP"
                style={{
                  position: "relative",
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  background: "#fff",
                  borderRadius: 8,
                  overflow: "hidden",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                  border: "1px solid #ececec",
                }}
              >
                {/* Badge distinctif de la version archivée */}
                <span
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: "#fff",
                    background: "rgba(0,0,0,0.35)",
                    borderRadius: 10,
                    padding: "2px 8px",
                  }}
                >
                  <i className="fa fa-archive mr-1" />
                  Archivée
                </span>
                {/* Header orange en biseau, façon document */}
                <div
                  style={{
                    height: 56,
                    background:
                      "linear-gradient(110deg, #f0a93c 0%, #e07b2a 100%)",
                    clipPath: "polygon(0 0, 100% 0, 100% 60%, 0 100%)",
                  }}
                />
                {/* Corps : icône fichier + infos */}
                <div
                  style={{
                    padding: "4px 16px 16px",
                    marginTop: -18,
                    textAlign: "center",
                  }}
                >
                  <i
                    className="fa fa-file-pdf-o"
                    style={{
                      fontSize: 40,
                      color: "#e07b2a",
                      background: "#fff",
                      borderRadius: 8,
                      padding: 6,
                    }}
                  />
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      marginTop: 8,
                      wordBreak: "break-all",
                    }}
                  >
                    {existingCp.fileName || "Conditions particulières"}
                  </div>
                  <div
                    className="text-muted"
                    style={{ fontSize: 12, marginTop: 6 }}
                  >
                    Enregistrée le
                    <br />
                    <strong>{formatCpDate(existingCp.createdAt)}</strong>
                  </div>
                  <div
                    style={{
                      marginTop: 12,
                      color: "#e07b2a",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    <i className="fa fa-download mr-2" />
                    Télécharger
                  </div>
                </div>
                {/* Footer noir en biseau, façon document */}
                <div
                  style={{
                    height: 32,
                    background: "#111",
                    clipPath: "polygon(0 40%, 100% 0, 100% 100%, 0 100%)",
                  }}
                />
              </a>
            </div>
          ) : (
            <p className="text-muted">
              Aucune CP n'a encore été enregistrée pour ce devis.
            </p>
          )}
        </aside>
      )}

      {canSave && (
        <EnvoyerPourSignatureModal
          isOpen={isSignatureModalOpen}
          client={client}
          onSubmit={envoyerEnSignature}
          onClose={() => setIsSignatureModalOpen(false)}
        />
      )}
    </div>
  );
};

export default QuotationPdf;
